import { useMemo, useState, type ReactNode } from "react";
import { Check, X } from "lucide-react";
import { ProposalCardFrame } from "./ProposalCardFrame";
import { ProposalDiffBlock } from "./ProposalDiffBlock.js";
import type {
  PlayerAIToolProposalPayload,
  PlayerAIWidgetActionHandler,
} from "./playerai.types";

/**
 * The Hoshin workshop Proposal cards, and the registry that selects one.
 *
 * Split out of `PlayerAIInlineWidgets` so the shared inline-widget module stays
 * about inline widgets: these four cards are one cohesive artifact with their
 * own preview parsers, their own shared frame, and one shared action row. They
 * remain DATA-BLIND like everything else in this package — each renders purely
 * from the proposal payload's preview, and every decision leaves through
 * `onAction`.
 */

export interface HoshinProposalCardProps {
  payload: PlayerAIToolProposalPayload;
  canApprove: boolean;
  canReject: boolean;
  onAction?: PlayerAIWidgetActionHandler;
}

/* ---------------- shared preview parsing ---------------- */

interface PreviewIdea {
  readonly id: string;
  readonly label: string;
}

interface PreviewTheme {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly ideas: readonly PreviewIdea[];
}

function previewIdeas(value: unknown): PreviewIdea[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item !== "object" || item === null) return [];
    const record = item as Record<string, unknown>;
    return typeof record.id === "string"
      ? [
          {
            id: record.id,
            label: typeof record.label === "string" ? record.label : "",
          },
        ]
      : [];
  });
}

function previewThemes(value: unknown): PreviewTheme[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item !== "object" || item === null) return [];
    const record = item as Record<string, unknown>;
    if (typeof record.name !== "string") return [];
    return [
      {
        key: typeof record.key === "string" ? record.key : record.name,
        name: record.name,
        description:
          typeof record.description === "string" ? record.description : "",
        ideas: previewIdeas(record.ideas),
      },
    ];
  });
}

/* ---------------- shared chrome ---------------- */

/**
 * The Apply / Regenerate / Discard row every Hoshin workshop card shares.
 *
 * Regenerate and Discard are one control pair on purpose: the request removed
 * the AI INITIATION buttons from the canvas, not the approval controls here.
 */
function HoshinProposalActions({
  payload,
  canApprove,
  canReject,
  applyLabel,
  applyDisabled = false,
  onAction,
  onApprove,
}: {
  payload: PlayerAIToolProposalPayload;
  canApprove: boolean;
  canReject: boolean;
  applyLabel: string;
  applyDisabled?: boolean;
  onAction?: PlayerAIWidgetActionHandler;
  /** Overrides the plain approve emit for a card that carries a selection. */
  onApprove?: () => void;
}) {
  if (!canApprove && !canReject) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {canApprove && (
        <button
          type="button"
          disabled={applyDisabled}
          onClick={() =>
            onApprove
              ? onApprove()
              : onAction?.(`toolProposal:approve:${payload.proposalId}`)
          }
          className="inline-flex items-center gap-1 rounded-md bg-pb-accent px-2 py-1 text-dense-1 font-medium text-pb-accent-foreground transition-colors hover:bg-pb-accent-hover active:bg-pb-accent-active disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Check className="h-3 w-3" />
          {applyLabel}
        </button>
      )}
      {canReject && (
        <>
          <button
            type="button"
            onClick={() =>
              onAction?.(`toolProposal:regenerate:${payload.proposalId}`)
            }
            className="inline-flex items-center gap-1 rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 font-medium text-pb-text-secondary hover:text-pb-text hover:border-pb-iris/40 transition-colors"
          >
            Regenerate
          </button>
          <button
            type="button"
            onClick={() =>
              onAction?.(`toolProposal:reject:${payload.proposalId}`)
            }
            className="inline-flex items-center gap-1 rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 font-medium text-pb-text-secondary hover:text-pb-text hover:border-pb-iris/40 transition-colors"
          >
            <X className="h-3 w-3" />
            Discard
          </button>
        </>
      )}
    </div>
  );
}

/** Title + status pill + description + error, shared by the workshop cards. */
function HoshinProposalFrame({
  payload,
  testId,
  children,
}: {
  payload: PlayerAIToolProposalPayload;
  testId: string;
  children: ReactNode;
}) {
  return (
    <ProposalCardFrame payload={payload} testId={testId}>
      {payload.description ? (
        <div className="mt-1.5 text-dense-1 leading-snug text-pb-text-secondary">
          {payload.description}
        </div>
      ) : null}
      <p className="mt-2 text-xs font-medium text-pb-text-secondary">
        Proposed change
      </p>
      {children}
      {payload.error ? (
        <div className="mt-2 rounded border border-pb-red/30 bg-pb-red-soft px-2 py-1.5 text-dense-1 leading-snug text-pb-red-text">
          {payload.error}
        </div>
      ) : null}
    </ProposalCardFrame>
  );
}

/* ---------------- grouping ---------------- */

/**
 * Dedicated shared-chat card for a Hoshin workshop grouping Proposal. Every
 * participant sees the same structured content; only the deciding driver gets
 * the Apply grouping / Regenerate / Discard controls (via canApprove/canReject).
 * Data-blind: everything renders from the proposal preview payload.
 */
function HoshinGroupingProposalCard({
  payload,
  canApprove,
  canReject,
  onAction,
}: HoshinProposalCardProps) {
  const preview = payload.preview ?? {};
  const themes = previewThemes(preview.themes);
  const ungrouped = previewIdeas(preview.ungrouped_ideas);
  const replaces = preview.replaces_existing_groups === true;
  const removedVotes =
    typeof preview.removed_vote_count === "number"
      ? preview.removed_vote_count
      : 0;
  return (
    <HoshinProposalFrame
      payload={payload}
      testId="hoshin-grouping-proposal-card"
    >
      <div className="mt-2 space-y-1">
        {themes.map((theme) => (
          <details
            key={theme.key}
            className="rounded border border-pb-border/70 bg-pb-shell/70 px-2 py-1.5"
          >
            <summary className="cursor-pointer text-dense-1 font-medium text-pb-text">
              {theme.name}
              <span className="ms-1.5 text-dense-2 font-normal text-pb-text-muted">
                {theme.ideas.length}{" "}
                {theme.ideas.length === 1 ? "idea" : "ideas"}
              </span>
            </summary>
            {theme.description ? (
              <div className="mt-1 text-dense-2 text-pb-text-secondary">
                {theme.description}
              </div>
            ) : null}
            <ul className="mt-1 list-disc space-y-0.5 ps-4 text-dense-2 text-pb-text-secondary">
              {theme.ideas.map((idea) => (
                <li key={idea.id}>{idea.label || idea.id}</li>
              ))}
            </ul>
          </details>
        ))}
      </div>
      {ungrouped.length > 0 ? (
        <details className="mt-1 rounded border border-pb-border/70 bg-pb-shell/70 px-2 py-1.5">
          <summary className="cursor-pointer text-dense-1 text-pb-text-secondary">
            Ungrouped ideas
            <span className="ms-1.5 text-dense-2 text-pb-text-muted">
              {ungrouped.length}
            </span>
          </summary>
          <ul className="mt-1 list-disc space-y-0.5 ps-4 text-dense-2 text-pb-text-secondary">
            {ungrouped.map((idea) => (
              <li key={idea.id}>{idea.label || idea.id}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {replaces ? (
        <div className="mt-2 rounded border border-pb-amber/40 bg-pb-amber-soft px-2 py-1.5 text-dense-1 leading-snug text-pb-text-secondary">
          Applying replaces the existing themes
          {removedVotes > 0
            ? ` and removes ${removedVotes} ${removedVotes === 1 ? "vote" : "votes"} cast for them.`
            : "."}
        </div>
      ) : null}
      <HoshinProposalActions
        payload={payload}
        canApprove={canApprove}
        canReject={canReject}
        applyLabel="Apply grouping"
        {...(onAction !== undefined ? { onAction } : {})}
      />
    </HoshinProposalFrame>
  );
}

/* ---------------- impact / effort ---------------- */

interface ImpactEffortPreviewRow {
  readonly id: string;
  readonly label: string;
  readonly groupName: string;
  readonly impact: number;
  readonly effort: number;
  readonly changes: boolean;
}

function impactEffortPreviewRows(value: unknown): ImpactEffortPreviewRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item !== "object" || item === null) return [];
    const record = item as Record<string, unknown>;
    if (
      typeof record.id !== "string" ||
      typeof record.impact !== "number" ||
      typeof record.effort !== "number"
    ) {
      return [];
    }
    return [
      {
        id: record.id,
        label: typeof record.label === "string" ? record.label : "",
        groupName:
          typeof record.group_name === "string" ? record.group_name : "",
        impact: record.impact,
        effort: record.effort,
        changes: record.changes !== false,
      },
    ];
  });
}

/**
 * Impact/effort scoring Proposal: the ideas with their proposed positions, and
 * — the part that matters before Apply — which of those positions would
 * actually change. An ordinary score leaves an already-placed idea alone, so
 * the card says "keeps position" rather than implying a move. An explicit
 * RESCORE is the other case, and must not borrow that copy: the room is about
 * to see positions they already agreed on move, so the card says so.
 */
function HoshinImpactEffortProposalCard({
  payload,
  canApprove,
  canReject,
  onAction,
}: HoshinProposalCardProps) {
  const preview = payload.preview ?? {};
  const targetNoun = preview.target_kind === "group" ? "group" : "idea";
  const rows = impactEffortPreviewRows(preview.placements);
  const changing = rows.filter((row) => row.changes);
  const rescore = preview.overwrite_existing === true;
  return (
    <HoshinProposalFrame
      payload={payload}
      testId="hoshin-impact-effort-proposal-card"
    >
      <div className="mt-2 space-y-1">
        {rows.map((row) => (
          <details
            key={row.id}
            className="rounded border border-pb-border/70 bg-pb-shell/70 px-2 py-1.5"
          >
            <summary className="cursor-pointer text-dense-1 text-pb-text [overflow-wrap:anywhere]">
              {row.label || (targetNoun === "group" ? "Group" : "Idea")}
            </summary>
            <ProposalDiffBlock
              side="added"
              label="Proposed change"
              className="mt-2"
            >
              <span className="text-dense-2 text-pb-text-muted">
                Impact {row.impact.toFixed(1)} · Effort {row.effort.toFixed(1)}
              </span>
            </ProposalDiffBlock>
            <div className="mt-0.5 text-dense-2 text-pb-text-muted">
              {row.groupName ? `${row.groupName} · ` : ""}
              {!row.changes
                ? "keeps its current position"
                : rescore
                  ? "moves to this position"
                  : "new placement"}
            </div>
          </details>
        ))}
      </div>
      {rows.length > 0 ? (
        <div className="mt-2 text-dense-2 text-pb-text-muted">
          {rescore
            ? `Only ${changing.length === 1 ? `this ${targetNoun}` : `these ${changing.length} ${targetNoun}s`} would move; every other position on the chart stays exactly as it is.`
            : `${changing.length} of ${rows.length} ${rows.length === 1 ? targetNoun : `${targetNoun}s`} would be placed; the rest keep the positions they already have.`}
        </div>
      ) : null}
      <HoshinProposalActions
        payload={payload}
        canApprove={canApprove}
        canReject={canReject}
        applyLabel="Apply placements"
        {...(onAction !== undefined ? { onAction } : {})}
      />
    </HoshinProposalFrame>
  );
}

/* ---------------- duplicates ---------------- */

interface DuplicatesPreviewSection {
  readonly sectionIndex: number;
  readonly relationship: string;
  readonly suggestedIdea: string;
  readonly originals: readonly PreviewIdea[];
}

function duplicatesPreviewSections(value: unknown): DuplicatesPreviewSection[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item !== "object" || item === null) return [];
    const record = item as Record<string, unknown>;
    if (
      typeof record.section_index !== "number" ||
      typeof record.suggested_idea !== "string"
    ) {
      return [];
    }
    return [
      {
        sectionIndex: record.section_index,
        relationship:
          typeof record.relationship === "string" ? record.relationship : "",
        suggestedIdea: record.suggested_idea,
        originals: previewIdeas(record.originals),
      },
    ];
  });
}

const DUPLICATE_RELATIONSHIP_LABEL: Record<string, string> = {
  exact_duplicate: "Exact duplicates",
  near_duplicate: "Near duplicates",
};

/**
 * Duplicate-review Proposal: the one card in the set that is an EDITOR.
 *
 * Every participant sees the same sections and the same original ideas; the
 * deciding driver additionally picks which replacements to keep and can reword
 * each one before Apply. Nothing here deletes an original — applying only ever
 * adds the chosen replacements as new ideas — so the card says so rather than
 * offering a "keep originals" choice that has no opposite.
 */
function HoshinDuplicatesProposalCard(props: HoshinProposalCardProps) {
  const preview = props.payload.preview ?? {};
  const sourceRevision =
    typeof preview.source_revision === "string" ? preview.source_revision : "";
  // Remount when the PROPOSAL changes, so the editor re-initialises from the
  // sections now on screen.
  //
  // Regenerate replaces the card in place: same mounted component, new
  // Proposal. `useState` initialisers only run on mount, so without this the
  // previous round's ticks and reworded text would survive onto sections that
  // no longer exist — and `chosen` would send `section_index` values the new
  // pinned command never offered. A `key` resets both pieces of state in one
  // move, with no state written during render and no effect; an unchanged
  // identity is an ordinary re-render, so the driver's in-progress edits are
  // untouched.
  return (
    <DuplicatesEditor
      key={`${props.payload.proposalId}:${sourceRevision}`}
      {...props}
    />
  );
}

function DuplicatesEditor({
  payload,
  canApprove,
  canReject,
  onAction,
}: HoshinProposalCardProps) {
  const preview = payload.preview ?? {};
  const sections = useMemo(
    () => duplicatesPreviewSections(preview.sections),
    [preview.sections],
  );
  const editable = canApprove;
  // Selected-by-default matches the reviewing flow: the driver deselects the
  // suggestions they disagree with rather than re-picking every good one.
  const [selected, setSelected] = useState<ReadonlySet<number>>(
    () => new Set(sections.map((section) => section.sectionIndex)),
  );
  const [drafts, setDrafts] = useState<Readonly<Record<number, string>>>(() =>
    Object.fromEntries(
      sections.map((section) => [section.sectionIndex, section.suggestedIdea]),
    ),
  );
  // A section the driver selected but then blanked carries no replacement to
  // add, so it is dropped here rather than sent as an empty idea the product
  // backend would refuse.
  const chosen = sections.filter(
    (section) =>
      selected.has(section.sectionIndex) &&
      (drafts[section.sectionIndex] ?? "").trim().length > 0,
  );
  return (
    <HoshinProposalFrame
      payload={payload}
      testId="hoshin-duplicates-proposal-card"
    >
      <div className="mt-2 space-y-1.5">
        {sections.map((section) => {
          const draft = drafts[section.sectionIndex] ?? "";
          const isSelected = selected.has(section.sectionIndex);
          return (
            <div
              key={section.sectionIndex}
              className="rounded border border-pb-border/70 bg-pb-shell/70 px-2 py-1.5"
            >
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={isSelected}
                  disabled={!editable}
                  onChange={() =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (next.has(section.sectionIndex)) {
                        next.delete(section.sectionIndex);
                      } else {
                        next.add(section.sectionIndex);
                      }
                      return next;
                    })
                  }
                />
                <span className="text-dense-1 font-medium text-pb-text">
                  {DUPLICATE_RELATIONSHIP_LABEL[section.relationship] ??
                    "Similar ideas"}
                </span>
              </label>
              <ul className="mt-1 list-disc space-y-0.5 ps-6 text-dense-2 text-pb-text-secondary">
                {section.originals.map((idea) => (
                  <li key={idea.id}>{idea.label || idea.id}</li>
                ))}
              </ul>
              <div className="mt-1.5 ps-6">
                <label
                  className="block text-dense-2 text-pb-text-muted"
                  htmlFor={`${payload.proposalId}-replacement-${section.sectionIndex}`}
                >
                  Replacement idea
                </label>
                <textarea
                  id={`${payload.proposalId}-replacement-${section.sectionIndex}`}
                  value={draft}
                  rows={2}
                  disabled={!editable || !isSelected}
                  onChange={(event) =>
                    setDrafts((current) => ({
                      ...current,
                      [section.sectionIndex]: event.target.value,
                    }))
                  }
                  className="mt-0.5 w-full rounded border border-pb-control-border bg-pb-card px-1.5 py-1 text-dense-1 text-pb-text disabled:opacity-60"
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 rounded border border-pb-border/70 bg-pb-shell/70 px-2 py-1.5 text-dense-2 leading-snug text-pb-text-muted">
        Applying adds the selected replacements as new ideas. The original ideas
        are kept exactly as they are.
      </div>
      <HoshinProposalActions
        payload={payload}
        canApprove={canApprove}
        canReject={canReject}
        applyLabel={
          chosen.length === 1 ? "Add 1 idea" : `Add ${chosen.length} ideas`
        }
        applyDisabled={chosen.length === 0}
        {...(onAction !== undefined ? { onAction } : {})}
        onApprove={() =>
          onAction?.(`toolProposal:approve:${payload.proposalId}`, {
            kind: "hoshin_workshop_duplicates",
            selections: chosen.map((section) => ({
              index: section.sectionIndex,
              text: (drafts[section.sectionIndex] ?? "").trim(),
            })),
          })
        }
      />
    </HoshinProposalFrame>
  );
}

/* ---------------- X-Matrix ---------------- */

interface XMatrixChangeRow {
  readonly op: string;
  readonly label: string;
}

function xMatrixChanges(value: unknown): XMatrixChangeRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item !== "object" || item === null) return [];
    const record = item as Record<string, unknown>;
    if (typeof record.label !== "string") return [];
    return [
      {
        op: typeof record.op === "string" ? record.op : "",
        label: record.label,
      },
    ];
  });
}

/**
 * X-Matrix change-set Proposal: one readable line per change.
 *
 * The card deliberately shows INTENT, not the operation payload — the room is
 * approving "archive Cut lead time", not a JSON object it cannot audit by eye.
 * Everyone in the workshop sees it; only the deciding driver gets the controls.
 */
function HoshinXMatrixProposalCard({
  payload,
  canApprove,
  canReject,
  onAction,
}: HoshinProposalCardProps) {
  const preview = payload.preview ?? {};
  const changes = xMatrixChanges(preview.changes);
  const summary = typeof preview.summary === "string" ? preview.summary : "";
  return (
    <HoshinProposalFrame
      payload={payload}
      testId="hoshin-x-matrix-proposal-card"
    >
      {summary ? (
        <div className="mt-1.5 text-dense-1 leading-snug text-pb-text-secondary">
          {summary}
        </div>
      ) : null}
      <ol className="mt-2 space-y-1">
        {changes.map((change, index) => (
          <li
            key={`${index}-${change.op}`}
            className="rounded border border-pb-border/70 bg-pb-shell/70 px-2 py-1.5 text-dense-1 text-pb-text"
          >
            {change.label}
          </li>
        ))}
      </ol>
      <div className="mt-2 text-dense-2 text-pb-text-muted">
        {changes.length} {changes.length === 1 ? "change" : "changes"} apply
        together, or not at all.
      </div>
      <HoshinProposalActions
        payload={payload}
        canApprove={canApprove}
        canReject={canReject}
        applyLabel={
          changes.length === 1
            ? "Apply 1 change"
            : `Apply ${changes.length} changes`
        }
        applyDisabled={changes.length === 0}
        {...(onAction !== undefined ? { onAction } : {})}
      />
    </HoshinProposalFrame>
  );
}

/**
 * Preview `kind` → a RENDERER for that card. A registry, not a conditional
 * chain (packages/ui invariant 3): a fifth workshop card is one component plus
 * one key here, and a preview kind with no entry falls through to the generic
 * proposal rendering.
 *
 * The values render a node; they are not component TYPES. Looking a type up and
 * mounting it as `<Resolved />` would create the element type during render
 * (`react-hooks/static-components`) and re-mount the card — losing the
 * duplicate editor's selections — whenever the lookup changed. Each entry
 * builds its element from a STATIC component, so the choice stays data-driven
 * while every card keeps its own hooks, state and identity.
 */
export type HoshinProposalCardRenderer = (
  props: HoshinProposalCardProps,
) => ReactNode;

export const HOSHIN_PROPOSAL_CARDS: Readonly<
  Record<string, HoshinProposalCardRenderer>
> = {
  hoshin_workshop_grouping: (props) => (
    <HoshinGroupingProposalCard {...props} />
  ),
  hoshin_workshop_impact_effort: (props) => (
    <HoshinImpactEffortProposalCard {...props} />
  ),
  hoshin_workshop_x_matrix: (props) => <HoshinXMatrixProposalCard {...props} />,
  hoshin_workshop_duplicates: (props) => (
    <HoshinDuplicatesProposalCard {...props} />
  ),
};

/** This proposal's dedicated card, or `null` when its kind has none. */
export function renderHoshinProposalCard(
  props: HoshinProposalCardProps,
): ReactNode {
  const kind = props.payload.preview?.kind;
  const render =
    typeof kind === "string" ? HOSHIN_PROPOSAL_CARDS[kind] : undefined;
  return render ? render(props) : null;
}
